import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexLegend,
    ApexNonAxisChartSeries,
    ApexPlotOptions,
    ApexResponsive,
    ApexTheme,
    ApexTitleSubtitle,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
    ChartComponent,
    NgApexchartsModule,
} from 'ng-apexcharts';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';
import { WorkflowAverageTimeDashboardItem } from 'src/app/models/server/workflow-average-time-dashboard-item';
import { WorkflowDelayDashboardItem } from 'src/app/models/server/workflow-delay-dashboard-item';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { DashboardService } from 'src/app/services/http/dashboard.service';
import { UsersService } from 'src/app/services/http/users.service';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DashboardWorkflowDelayOptions } from 'src/app/models/server/dashboard-workflow-delay-options';
import { DashboardWorkflowStepState } from 'src/app/models/server/enums/dashboard-workflow-step-state';
import { View } from 'src/app/models/server/view';

export type ChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    plotOptions: ApexPlotOptions;
    responsive: ApexResponsive[];
    labels: any;
    colors: any[];
    dataLabels: ApexDataLabels;
    yaxis: ApexYAxis;
    fill: ApexFill;
    title: ApexTitleSubtitle;
};

export type AxisChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    plotOptions: ApexPlotOptions;
    responsive: ApexResponsive[];
    labels: any;
    colors: any[];
    dataLabels: ApexDataLabels;
    xaxis: ApexXAxis;
    yaxis: ApexXAxis;
    fill: ApexFill;
    title: ApexTitleSubtitle;
    legend: ApexLegend;
    theme: ApexTheme;
    tooltip: ApexTooltip;
};

export type serieItem = {
    name: string;
    data: number[];
};

@Component({
    selector: 'app-supervision',
    templateUrl: './supervision.component.html',
    styleUrls: ['./supervision.component.scss'],
    imports: [MatCardModule, MatFormFieldModule, MatSelectModule, MatOptionModule, NgApexchartsModule, NgxSpinnerModule, MatButtonModule],
})
export class SupervisionComponent implements OnInit, OnDestroy {
    public DashboardWorkflowStepState = DashboardWorkflowStepState;
    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    @ViewChild('histochart') chart: ChartComponent;

    public charts: Partial<ChartOptions>[] = [];

    public histoChart: Partial<AxisChartOptions> = null;

    /**
     * Liste des vues.
     */
    public views: View[];

    private selectedViewId: number;

    private selectedDashboardType: string;

    private selectedStepType: DashboardWorkflowStepState;

    /**
     * Constructeur.
     * @param dashboardService
     * @param dialogRef
     * @param data : Données transitant entre l'appelant de la boite de dialogue et la boite de dialogue (En entrée et en sortie)
     */
    constructor(
        private dashboardService: DashboardService,
        private usersService: UsersService,
        private spinner: SpinnerService,
        public dialogRef: MatDialogRef<SupervisionComponent>
    ) {}

    ngOnInit(): void {
        // Récupère les workflows.
        this.loadViews();
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Charge les définitions d'attributs en mémoire.
     */
    private loadDatas() {
        if (this.selectedViewId && this.selectedDashboardType && this.selectedStepType) {
            if (this.selectedDashboardType === 'Wokflow_delay') {
                this.spinner.show('supervision-spinner');

                let options = new DashboardWorkflowDelayOptions();
                options.workflowStepState = this.selectedStepType;

                this.dashboardService
                    .GetWorkflowDashboard<WorkflowDelayDashboardItem>(this.selectedDashboardType, this.selectedViewId, options)
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe((x) => {
                        this.loadDatasDelay(x.datas);
                        this.spinner.hide('supervision-spinner');
                    });
            } else if (this.selectedDashboardType === 'Wokflow_average_time') {
                this.spinner.show('supervision-spinner');

                let options = new DashboardWorkflowDelayOptions();
                options.workflowStepState = this.selectedStepType;

                this.dashboardService
                    .GetWorkflowDashboard<WorkflowAverageTimeDashboardItem>(this.selectedDashboardType, this.selectedViewId, options)
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe((x) => {
                        this.loadDatasAverageTime(x.datas, x.unit);
                        this.spinner.hide('supervision-spinner');
                    });
            }
        }
    }

    private loadDatasDelay(x: WorkflowDelayDashboardItem[]) {
        let charts = [];

        this.charts = charts;

        let series: serieItem[] = [];
        let serieCount = [];
        let serieWarningStepCount = [];
        let serieLateStepCount = [];
        let categories = [];

        x.forEach((item) => {
            serieCount.push(item.count);
            serieWarningStepCount.push(item.warningStepCount);
            serieLateStepCount.push(item.lateStepCount);
            categories.push(item.stepName);
        });

        series.push({ name: 'Conforme', data: serieCount });
        series.push({
            name: 'Proche du délai max',
            data: serieWarningStepCount,
        });
        series.push({ name: 'En retard', data: serieLateStepCount });

        this.histoChart = {
            title: {
                text: 'Respect des délais par étape.',
                align: 'center',
                style: {
                    color: '#FFF',
                    fontSize: '16px',
                    fontWeight: 900,
                },
            },
            series: series,
            chart: {
                type: 'bar',
                height: 500,
                stacked: true,
                toolbar: {
                    show: true,
                },
                zoom: {
                    enabled: true,
                },
            },
            colors: ['#B6E2A1', '#FFDCA9', '#FAAB78'],
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            plotOptions: {
                bar: {
                    horizontal: false,
                    dataLabels: {
                        total: {
                            enabled: true,
                            style: {
                                color: '#FFF',
                                fontSize: '13px',
                                fontWeight: 900,
                            },
                        },
                    },
                },
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '12px',
                    colors: ['var(--avm-color-36)'],
                },
                formatter: function (val, opts) {
                    const numberVal = val as number;
                    if (Math.abs(numberVal) === numberVal) {
                        return numberVal;
                    } else {
                        return (val as number).toFixed(1);
                    }
                },
            },

            xaxis: {
                type: 'category',
                categories: categories,
                tickPlacement: 'on',
                labels: {
                    show: true,
                    rotate: -30,
                    rotateAlways: true,
                    style: {
                        colors: '#FFF',
                    },
                },
            },
            yaxis: {
                title: {
                    text: 'Nombre',
                    style: {
                        color: '#FFF',
                    },
                },
                decimalsInFloat: 1,
                labels: {
                    show: true,
                    style: {
                        colors: '#FFF',
                    },
                },
            },
            legend: {
                show: true,
                position: 'bottom',
                markers: {},
                itemMargin: {
                    vertical: 50,
                },
                labels: {
                    colors: 'var(--light)',
                    useSeriesColors: true,
                },
            },
            fill: {
                opacity: 1,
            },
            theme: {
                mode: 'dark',
            },
            tooltip: {
                shared: true,
                hideEmptySeries: false,
            },
        };
    }

    private loadDatasAverageTime(x: WorkflowAverageTimeDashboardItem[], unit: string) {
        let charts = [];

        this.charts = charts;

        let series: serieItem[] = [];
        let serieMin = [];
        let serieAverage = [];
        let serieMax = [];
        let expectedValue = [];
        let categories = [];

        // {
        //     x: any;
        //     y: any;
        //     fill?: ApexFill;
        //     fillColor?: string;
        //     strokeColor?: string;
        //     meta?: any;
        //     goals?: {
        //         name?: string;
        //         value: number;
        //         strokeHeight?: number;
        //         strokeWidth?: number;
        //         strokeColor?: string;
        //         strokeDashArray?: number;
        //         strokeLineCap?: "butt" | "square" | "round";
        //     }[];
        //     barHeightOffset?: number;
        //     columnWidthOffset?: number;
        // }

        x.forEach((item) => {
            serieMin.push({
                x: item.stepName,
                y: item.min,
                // goals: [{ name: 'Durée attendue', value: 45, strokeColor: '#D0C1FF', strokeLineCap: 'round', strokeHeight: 5, strokeWidth: 30, strokeDashArray: 5 }], // Expected value
            });
            serieAverage.push({
                x: item.stepName,
                y: item.average,
                goals: [{ name: 'Durée attendue', value: item.expected, strokeColor: '#D0C1FF', strokeLineCap: 'round', strokeHeight: 5, strokeWidth: 30, strokeDashArray: 8 }],
            });
            serieMax.push({
                x: item.stepName,
                y: item.max,
                // goals: [{ name: 'Durée attendue', value: 45, strokeColor: '#D0C1FF', strokeLineCap: 'round', strokeHeight: 5, strokeWidth: 30, strokeDashArray: 5 }],
            });

            categories.push(item.stepName);
        });

        series.push({ name: 'Durée minimale', data: serieMin });
        series.push({ name: 'Durée moyenne', data: serieAverage });
        series.push({ name: 'Durée maximale', data: serieMax });

        this.histoChart = {
            title: {
                text: 'Délais moyen par étape.',
                align: 'center',
                style: {
                    color: '#FFF',
                    fontSize: '16px',
                    fontWeight: 900,
                },
            },
            series: series,
            chart: {
                type: 'bar',
                height: 600,
                stacked: false,
                toolbar: {
                    show: true,
                },
                zoom: {
                    enabled: true,
                },
            },
            colors: ['#B6E2A1', '#FFDCA9', '#FAAB78'],
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            plotOptions: {
                bar: {
                    horizontal: false,
                    dataLabels: {
                        total: {
                            enabled: false,
                            style: {
                                color: '#FFF',
                                fontSize: '13px',
                                fontWeight: 900,
                            },
                        },
                    },
                    hideZeroBarsWhenGrouped: false,
                },
            },
            dataLabels: {
                enabled: false,
            },

            xaxis: {
                type: 'category',
                categories: categories,
                tickPlacement: 'on',
                labels: {
                    show: true,
                    rotate: -30,
                    rotateAlways: true,
                    style: {
                        colors: '#FFF',
                    },
                },
            },
            yaxis: {
                title: {
                    text: unit,
                    style: {
                        color: '#FFF',
                    },
                },
                decimalsInFloat: 1,
                labels: {
                    show: true,
                    style: {
                        colors: '#FFF',
                    },
                },
            },
            legend: {
                show: true,
                position: 'bottom',
                itemMargin: {
                    vertical: 50,
                },
                labels: {
                    colors: 'var(--light)',
                    useSeriesColors: true,
                },
                customLegendItems: ['Durée minimale', 'Durée moyenne', 'Durée maximale', 'Durée attendue'],
                markers: {
                    fillColors: ['#B6E2A1', '#FFDCA9', '#FAAB78', '#D0C1FF'],
                },
            },
            fill: {
                opacity: 1,
            },
            theme: {
                mode: 'dark',
            },
            tooltip: {
                shared: true,
                hideEmptySeries: false,
            },
        };
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Fermer'.
     */
    public close(): void {
        this.dialogRef.close();
    }

    public onWorkflowChange(event: MatSelectChange): void {
        this.selectedViewId = event.value;
        this.loadDatas();
    }

    public onDashboardChange(event: MatSelectChange): void {
        this.selectedDashboardType = event.value;
        this.loadDatas();
    }

    public onStepTypeChange(event: MatSelectChange): void {
        this.selectedStepType = event.value;
        this.loadDatas();
    }

    /**
     * Charge les vues en mémoire.
     */
    private loadViews() {
        this.usersService
            .getViews()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.views = x;
            });
    }
}
